import HandoverPeriodControl from '../../features/preparationJourneys/HandoverPeriodControl';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, ButtonBase, Container, Paper, Stack, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import {
  ArrowBackRounded as BackIcon,
} from '@mui/icons-material';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import colors from '../../assets/theme/colors';
import { useChildContext } from '../../contexts/ChildContext';
import { useRole } from '../../contexts/RoleContext';
import { canEditChildProfile } from '../../constants/roles';
import { CARE_SNAPSHOT_STEPS } from '../../constants/careSnapshotSections';
import { ACTIVE_CHILD_STORAGE_KEY } from '../../components/Dashboard/shared/DashboardViewContext';
import ChildShellBottomNav from '../../components/Dashboard/shared/ChildShellBottomNav';
import TrackerSourceSheet from '../../components/Dashboard/shared/TrackerSourceSheet';
import { getChildAccentTheme } from '../../components/Dashboard/shared/childAccentTheme';
import AskRecordThinkingState from '../../components/AskRecord/AskRecordThinkingState';
import PreparationJourneyArea from '../../features/preparationJourneys/PreparationJourneyArea';
import GuidedPreparationQuestions from '../../features/preparationJourneys/GuidedPreparationQuestions';
import CustomJourneySupportingLink from '../../features/preparationJourneys/CustomJourneySupportingLink';
import CustomJourneyOverviewDetails from '../../features/preparationJourneys/CustomJourneyOverviewDetails';
import CustomJourneyHelpfulDocuments from '../../features/preparationJourneys/CustomJourneyHelpfulDocuments';
import PreparationProfileSourceSheet from '../../features/preparationJourneys/PreparationProfileSourceSheet';
import PreparationReferenceSheet from '../../features/preparationJourneys/PreparationReferenceSheet';
import JourneyEvidencePeriodControl from '../../features/preparationJourneys/JourneyEvidencePeriodControl';
import JourneySynthesisPanel from '../../features/preparationJourneys/JourneySynthesisPanel';
import JourneyBriefPanel from '../../features/preparationJourneys/JourneyBriefPanel';
import PreparationLifecycleMenu from '../../features/preparationJourneys/PreparationLifecycleMenu';
import { resolvePreparationReferences } from '../../features/preparationJourneys/preparationJourneyReferences';
import {
  upsertEvidenceDecision,
} from '../../features/preparationJourneys/preparationDecisionModel';
import {
  archivePreparationDraft,
  getPreparationDraft,
  makePreparationAnswer,
  makePreparationDecision,
  restorePreparationDraft,
  savePreparationDraft,
} from '../../features/preparationJourneys/preparationDraftService';
import { getArchivedPreparationLabel } from '../../features/preparationJourneys/preparationArchiveModel';
import { upsertJourneyAnswer } from '../../features/preparationJourneys/journeyAnswerModel';
import useProgressiveJourneySnapshot from '../../features/preparationJourneys/useProgressiveJourneySnapshot';
import { correctCustomJourneyPlan } from '../../services/guidedCareJourneyService';
import { auth } from '../../services/firebase';

const IEP_DRAFT_ID = 'iep_meeting-v3-current';
const IEP_TEMPLATE_VERSION = 3;
const IEP_DRAFT_IDENTITY = Object.freeze({
  draftId: IEP_DRAFT_ID,
  journeyType: 'iep_meeting',
  templateId: 'iep_meeting',
  templateVersion: IEP_TEMPLATE_VERSION,
});

const PROFILE_ADD_STEP_BY_ACTION = {
  profile_goals: CARE_SNAPSHOT_STEPS.GOALS_GROWTH,
  profile_daily_support: CARE_SNAPSHOT_STEPS.DAILY_SUPPORT,
  profile_patterns: CARE_SNAPSHOT_STEPS.PATTERNS_RESPONSES,
  profile_medications: CARE_SNAPSHOT_STEPS.HEALTH_CARE,
  profile_documents: CARE_SNAPSHOT_STEPS.HEALTH_CARE,
};

const IepGuidedCareJourneyPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { childId: routeChildId, draftId: routeDraftId } = useParams();
  const { currentChildId, setCurrentChildId } = useChildContext();
  const { childrenWithAccess = [], getUserRoleForChild, loading: roleLoading } = useRole();
  const storedChildId = typeof window === 'undefined'
    ? '' : window.localStorage.getItem(ACTIVE_CHILD_STORAGE_KEY) || '';
  const childId = routeChildId || currentChildId || storedChildId || childrenWithAccess[0]?.id || '';
  const child = childrenWithAccess.find((item) => item.id === childId) || null;
  const accent = useMemo(
    () => getChildAccentTheme(child || {}, { children: childrenWithAccess }),
    [child, childrenWithAccess]
  );
  const isCustomJourney = Boolean(routeDraftId);
  const userRole = getUserRoleForChild?.(childId);
  const canAdd = canEditChildProfile(userRole);
  const activeDraftIdentity = useMemo(() => isCustomJourney ? ({
    draftId: routeDraftId,
    journeyType: 'custom_prepare',
    templateId: 'custom_prepare',
    templateVersion: 1,
  }) : IEP_DRAFT_IDENTITY, [isCustomJourney, routeDraftId]);
  const {
    snapshot, setSnapshot, loading, enhancing, enhancementError, error,
    refreshSnapshot: refreshJourneySnapshot,
  } = useProgressiveJourneySnapshot(canAdd ? childId : '', {
    journeyId: activeDraftIdentity.templateId,
    draftId: activeDraftIdentity.draftId,
  });
  const [expandedAreaId, setExpandedAreaId] = useState(() => location.state?.expandedJourneyAreaId || '');
  const [showAllEventsByArea, setShowAllEventsByArea] = useState({});
  const [sourcesExpandedByArea, setSourcesExpandedByArea] = useState({});
  const [selectedSource, setSelectedSource] = useState(null);
  const [selectedProfileItem, setSelectedProfileItem] = useState(null);
  const [referenceSources, setReferenceSources] = useState([]);
  const [draft, setDraft] = useState(null);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftError, setDraftError] = useState('');
  const [evidenceDecisionNotice, setEvidenceDecisionNotice] = useState('');
  const [decidingSourceId, setDecidingSourceId] = useState('');
  const [correctingPlan, setCorrectingPlan] = useState(false);
  const [revisionStatus, setRevisionStatus] = useState({ hasFinalizedRevision: false, changedSinceFinalized: false });
  const supportingInformationRef = useRef(null);
  const journeyPath = isCustomJourney
    ? `/children/${childId}/journeys/custom/${routeDraftId}`
    : `/children/${childId}/journeys/iep-meeting`;
  const canEditPreparation = canAdd && !draft?.isArchived;
  const confirmedPriorities = useMemo(() => draft?.confirmedPriorities || [], [draft?.confirmedPriorities]);
  const parentAreaHasServerReview = snapshot?.areas?.some((area) => (
    area.id === 'parent_priorities' && area.status !== 'not_found'
  ));
  const reviewAreaCount = (snapshot?.reviewCount ?? snapshot?.foundCount ?? 0)
    + (confirmedPriorities.length && !parentAreaHasServerReview ? 1 : 0);
  const scrollToSupportingInformation = useCallback(() => {
    supportingInformationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);
  const setArchivedState = useCallback(async (isArchived) => {
    setDraftError('');
    try {
      const update = isArchived
        ? await archivePreparationDraft({ childId, draftId: activeDraftIdentity.draftId })
        : await restorePreparationDraft({ childId, draftId: activeDraftIdentity.draftId });
      setDraft((current) => ({ ...(current || {}), ...update }));
      return true;
    } catch (archiveError) {
      setDraftError(archiveError?.message || 'This preparation could not be updated.');
      return false;
    }
  }, [activeDraftIdentity.draftId, childId]);
  useEffect(() => {
    if (childId && childId !== currentChildId) setCurrentChildId(childId);
  }, [childId, currentChildId, setCurrentChildId]);

  useEffect(() => {
    setRevisionStatus({ hasFinalizedRevision: false, changedSinceFinalized: false });
  }, [activeDraftIdentity.draftId]);

  useEffect(() => {
    if (!childId) return undefined;
    let active = true;
    setDraftLoaded(false);
    getPreparationDraft({ childId, draftId: activeDraftIdentity.draftId })
      .then((savedDraft) => active && setDraft(savedDraft))
      .catch(() => active && setDraft(null))
      .finally(() => active && setDraftLoaded(true));
    return () => { active = false; };
  }, [activeDraftIdentity.draftId, childId]);

  const goToDashboard = useCallback((target = 'tools') => navigate('/dashboard', {
    state: { selectedChildId: childId, childShellTarget: target },
  }), [childId, navigate]);

  const buildJourneyReturnState = useCallback((areaId) => ({
    expandedJourneyAreaId: areaId,
  }), []);

  const openEvidence = useCallback((area, item) => {
    const inPageSource = item.sourceRef || (
      ['timeline', 'dailyLog'].includes(item.sourceType)
        ? { type: 'timelineEntry', id: item.sourceId }
        : item.sourceType === 'document' ? { type: 'careSource', id: item.sourceId } : null
    );
    if (inPageSource?.type === 'timelineEntry' || inPageSource?.type === 'careSource') {
      setSelectedSource(inPageSource);
      return;
    }

    if (['profile', 'goal', 'knowledge', 'medication'].includes(item.sourceType)) {
      setSelectedProfileItem({ area, item });
      return;
    }

    const target = item.navigationTarget;
    if (!target?.route) return;
    navigate(target.route, {
      state: {
        ...(target.state || {}),
        childId,
        profileReturnTo: journeyPath,
        profileReturnState: buildJourneyReturnState(area.id),
      },
    });
  }, [buildJourneyReturnState, childId, journeyPath, navigate]);

  const allEvidence = useMemo(() => snapshot?.areas?.flatMap((area) => [
    ...(area.evidence || []), ...(area.possibleEvidence || []),
  ]) || [], [snapshot]);
  const areaBySourceId = useMemo(() => new Map(snapshot?.areas?.flatMap((area) => [
    ...(area.evidence || []), ...(area.possibleEvidence || []),
  ].map((item) => [`${item.sourceType}:${item.sourceId}`, area])) || []), [snapshot]);
  const openReferences = useCallback((area, sourceIds) => {
    const resolved = resolvePreparationReferences(sourceIds, allEvidence);
    if (resolved.length === 1) {
      openEvidence(area || areaBySourceId.get(`${resolved[0].sourceType}:${resolved[0].sourceId}`), resolved[0]);
      return;
    }
    if (resolved.length > 1) setReferenceSources(resolved.map((item) => ({
      ...item,
      area: area || areaBySourceId.get(`${item.sourceType}:${item.sourceId}`),
    })));
  }, [allEvidence, areaBySourceId, openEvidence]);

  const editSelectedProfileItem = useCallback(() => {
    const selection = selectedProfileItem;
    if (!selection) return;
    setSelectedProfileItem(null);
    const target = selection.item.navigationTarget;
    if (!target?.route) return;
    navigate(target.route, {
      state: {
        ...(target.state || {}),
        childId,
        profileReturnTo: journeyPath,
        profileReturnState: buildJourneyReturnState(selection.area.id),
      },
    });
  }, [buildJourneyReturnState, childId, journeyPath, navigate, selectedProfileItem]);

  const savePreviousIepDate = useCallback(async (previousIepDate) => {
    setSavingDraft(true);
    setDraftError('');
    try {
      await savePreparationDraft({ childId, ...activeDraftIdentity, previousIepDate });
      setDraft((current) => ({ ...(current || {}), previousIepDate: previousIepDate || null }));
      await refreshJourneySnapshot();
    } catch (saveError) {
      setDraftError(saveError?.message || 'The previous IEP date could not be saved.');
      return false;
    } finally {
      setSavingDraft(false);
    }
    return true;
  }, [activeDraftIdentity, childId, refreshJourneySnapshot]);

  const decideEvidence = useCallback(async (area, item, status) => {
    const sourceId = `${item.sourceType}:${item.sourceId}`;
    setDecidingSourceId(sourceId);
    setDraftError('');
    setEvidenceDecisionNotice('');
    try {
      const evidenceDecisions = upsertEvidenceDecision({
        existingDecisions: draft?.evidenceDecisions || [],
        areaId: area.id,
        sourceId,
        status,
        makeDecision: makePreparationDecision,
      });
      await savePreparationDraft({
        childId,
        ...activeDraftIdentity,
        confirmedPriorities,
        suggestionDecisions: draft?.suggestionDecisions || [],
        evidenceDecisions,
      });
      setDraft((current) => ({ ...(current || {}), evidenceDecisions }));
      setSnapshot((current) => ({
        ...current,
        areas: current.areas.map((currentArea) => currentArea.id !== area.id ? currentArea : {
          ...currentArea,
          possibleEvidence: (currentArea.possibleEvidence || []).filter((candidate) => `${candidate.sourceType}:${candidate.sourceId}` !== sourceId),
          ...(status === 'accepted' ? { evidence: [...currentArea.evidence, { ...item, relevance: 'include' }] } : {}),
        }),
      }));
      const refreshedSnapshot = await refreshJourneySnapshot();
      if (status === 'accepted') {
        setEvidenceDecisionNotice(refreshedSnapshot?.answerDrafting?.status === 'failed'
          ? 'Added to preparation. We could not refresh the suggested answers yet.'
          : 'Added to preparation. Suggested answers were refreshed where relevant.');
      } else {
        setEvidenceDecisionNotice('Left out of this preparation.');
      }
    } catch (saveError) {
      setDraftError(saveError?.message || 'This evidence decision could not be saved.');
    } finally {
      setDecidingSourceId('');
    }
  }, [activeDraftIdentity, childId, confirmedPriorities, draft?.evidenceDecisions, draft?.suggestionDecisions, refreshJourneySnapshot, setSnapshot]);

  const handleAdd = useCallback((area) => {
    const returnToState = buildJourneyReturnState(area.id);
    if (area.addAction === 'timeline_entry') {
      navigate('/dashboard', {
        state: {
          selectedChildId: childId,
          openQuickEntryForChildId: childId,
          returnTo: journeyPath,
          returnToState,
        },
      });
      return;
    }

    const initialStep = PROFILE_ADD_STEP_BY_ACTION[area.addAction];
    if (!initialStep) return;
    navigate('/care-profile', {
      state: {
        childId,
        initialStep,
        profileReturnTo: journeyPath,
        profileReturnState: returnToState,
      },
    });
  }, [buildJourneyReturnState, childId, journeyPath, navigate]);

  const uploadHelpfulDocument = useCallback(() => {}, []);

  const handleBottomNav = (itemId) => {
    if (itemId === 'home') return navigate('/home', { state: { selectedChildId: childId } });
    if (itemId === 'profile') return navigate('/care-profile', { state: { childId } });
    return goToDashboard(itemId === 'tools' ? 'tools' : 'today');
  };

  const updateCustomPlan = useCallback(async () => {
    setCorrectingPlan(true);
    setDraftError('');
    try {
      await correctCustomJourneyPlan({ childId, draftId: activeDraftIdentity.draftId });
      await refreshJourneySnapshot();
      const savedDraft = await getPreparationDraft({ childId, draftId: activeDraftIdentity.draftId });
      setDraft(savedDraft);
    } catch (updateError) {
      setDraftError(updateError?.message || 'The preparation plan could not be updated.');
    } finally {
      setCorrectingPlan(false);
    }
  }, [activeDraftIdentity.draftId, childId, refreshJourneySnapshot]);

  const updateBriefAnswer = useCallback(async (questionId, confirmedText) => {
    const existingAnswer = (draft?.answers || []).find((answer) => answer.questionId === questionId);
    if (!existingAnswer || existingAnswer.status !== 'confirmed' || !confirmedText.trim()) return false;
    setSavingDraft(true);
    setDraftError('');
    try {
      const updatedAnswer = makePreparationAnswer({
        ...existingAnswer,
        confirmedText: confirmedText.trim(),
        status: 'confirmed',
        origin: existingAnswer.origin,
      });
      const answers = upsertJourneyAnswer(draft?.answers || [], updatedAnswer);
      await savePreparationDraft({ childId, ...activeDraftIdentity, answers });
      setDraft((current) => ({ ...(current || {}), answers }));
      return true;
    } catch (saveError) {
      setDraftError(saveError?.message || 'This brief change could not be saved.');
      return false;
    } finally {
      setSavingDraft(false);
    }
  }, [activeDraftIdentity, childId, draft?.answers]);

  const updateBriefHeader = useCallback(async (briefHeader) => {
    setSavingDraft(true);
    setDraftError('');
    try {
      await savePreparationDraft({ childId, ...activeDraftIdentity, briefHeader });
      setDraft((current) => ({ ...(current || {}), briefHeader }));
      return true;
    } catch (saveError) {
      setDraftError(saveError?.message || 'This report heading could not be saved.');
      return false;
    } finally {
      setSavingDraft(false);
    }
  }, [activeDraftIdentity, childId]);

  if (!roleLoading && (!child || !canAdd)) return <Navigate to="/dashboard" replace />;

  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: colors.semantic.sageMist, pb: { xs: 10, md: 3 } }}>
      <Container maxWidth="md" sx={{ py: { xs: 1.4, md: 3 } }}>
        <ButtonBase onClick={() => goToDashboard('tools')} sx={{ mb: 1.3, gap: 0.45, color: colors.brand.deep, borderRadius: '10px', px: 0.5, py: 0.4 }}>
          <BackIcon sx={{ fontSize: 19 }} />
          <Typography sx={{ fontSize: '0.86rem', fontWeight: 800 }}>Back to Insights</Typography>
        </ButtonBase>

        {loading || roleLoading ? (
          <Box sx={{ minHeight: 300, display: 'grid', placeItems: 'center' }}>
            <AskRecordThinkingState firstName={child?.name} />
          </Box>
        ) : error ? (
          <Paper elevation={0} sx={{ p: 2, borderRadius: '22px', border: `1px solid ${alpha('#9E3A3A', 0.2)}` }}>
            <Typography sx={{ fontWeight: 850, color: '#8F3333' }}>{error}</Typography>
          </Paper>
        ) : snapshot ? (
          <Stack spacing={1.4}>
            {enhancing && !draft?.isArchived ? (
              <Box sx={{ px: 0.5 }}>
                <AskRecordThinkingState compact firstName={child?.name} />
              </Box>
            ) : null}
            <Paper elevation={0} sx={{ p: { xs: 1.7, md: 2.3 }, borderRadius: '24px', border: `1px solid ${alpha(accent.primary, 0.22)}` }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
                <Box>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 750, letterSpacing: '0.12em', textTransform: 'uppercase', color: accent.ink }}>
                    Guided care journey
                  </Typography>
                  <Typography component="h1" sx={{ mt: 0.55, fontSize: { xs: '1.55rem', md: '2rem' }, fontWeight: 800, lineHeight: 1.08, color: colors.semantic.ink }}>
                    {snapshot.title}
                  </Typography>
                </Box>
                <PreparationLifecycleMenu
                  draft={draft}
                  canManage={canAdd && draftLoaded && Boolean(draft)}
                  onArchive={() => setArchivedState(true)}
                  onRestore={() => setArchivedState(false)}
                />
              </Box>
              {draft?.isArchived ? (
                <Typography role="status" sx={{ mt: 0.8, fontSize: '0.82rem', fontWeight: 800, color: accent.ink }}>
                  {getArchivedPreparationLabel(draft)} · Read only
                </Typography>
              ) : null}
              <Typography sx={{ mt: 0.85, fontSize: { xs: '0.98rem', md: '1.08rem' }, lineHeight: 1.45, color: alpha(colors.semantic.ink, 0.72) }}>
                Lifelog found information that may help you prepare.
              </Typography>
              {isCustomJourney ? (
                <CustomJourneyOverviewDetails
                  answers={draft?.answers || []}
                  revisionStatus={revisionStatus}
                  gathering={enhancing && !draft?.isArchived}
                  gatheringFailed={enhancementError}
                  reviewedPeriodText={snapshot.evidenceHorizon?.reviewedPeriodText}
                  accent={accent}
                  onReviewSupporting={scrollToSupportingInformation}
                />
              ) : (
                <Typography sx={{ mt: 0.45, fontSize: { xs: '0.86rem', md: '0.94rem' }, lineHeight: 1.45, color: alpha(colors.semantic.ink, 0.62) }}>
                  {reviewAreaCount} of {snapshot.totalCount} areas have information you may want to review or bring to the meeting.
                </Typography>
              )}
              {snapshot.evidenceWarnings?.length ? <Alert severity="warning">Some source-backed facts need review because their source changed. They are excluded from new suggestions until reviewed in the Care Profile.</Alert> : null}
              <HandoverPeriodControl key={`${childId}:${activeDraftIdentity.draftId}`} period={snapshot.evidenceHorizon} coverage={snapshot.handoverCoverage}
                childId={childId} draftIdentity={activeDraftIdentity} canEdit={canEditPreparation} onSaved={refreshJourneySnapshot} />
              {!isCustomJourney ? <JourneyEvidencePeriodControl
                reviewedPeriodText={snapshot.evidenceHorizon?.reviewedPeriodText || 'Lifelog reviewed your current Profile and recent Timeline entries.'}
                previousIepDate={draft?.previousIepDate || snapshot.evidenceHorizon?.confirmedStartDate || ''}
                canEdit={canEditPreparation}
                saving={savingDraft}
                onSave={savePreviousIepDate}
              /> : null}
              {draftError ? (
                <Typography role="alert" sx={{ mt: 0.5, fontSize: '0.76rem', color: '#8F3333' }}>
                  {draftError}
                </Typography>
              ) : null}
              {evidenceDecisionNotice ? (
                <Typography role="status" sx={{ mt: 0.5, fontSize: '0.78rem', color: alpha(colors.semantic.ink, 0.68) }}>
                  {evidenceDecisionNotice}
                </Typography>
              ) : null}
              {isCustomJourney && canEditPreparation && snapshot.journeyPlan?.answerabilityCorrectionAvailable ? (
                <ButtonBase
                  disabled={correctingPlan}
                  onClick={updateCustomPlan}
                  sx={{ mt: 0.9, color: accent.ink, fontSize: '0.82rem', fontWeight: 800 }}
                >
                  {correctingPlan ? 'Updating preparation plan…' : 'Update preparation plan'}
                </ButtonBase>
              ) : null}
            </Paper>

            {draftLoaded ? (
              <GuidedPreparationQuestions
                childId={childId}
                draftIdentity={activeDraftIdentity}
                questions={snapshot.questions || []}
                proposedAnswers={snapshot.answerDrafts || []}
                evidence={allEvidence}
                answerFreshness={snapshot.answerFreshness || []}
                savedAnswers={draft?.answers || []}
                canEdit={canEditPreparation}
                accent={accent}
                onSaved={(answers) => setDraft((current) => ({ ...(current || {}), answers }))}
                onViewSources={(sourceIds) => openReferences(null, sourceIds)}
                presentation={isCustomJourney ? 'custom_cards' : 'default'}
              />
            ) : null}

            {isCustomJourney ? (
              <CustomJourneyHelpfulDocuments
                suggestions={snapshot.journeyPlan?.documentSuggestions || []}
                evidence={allEvidence}
                canAdd={canEditPreparation}
                accent={accent}
                onUpload={uploadHelpfulDocument}
                onOpenDocument={(item) => openEvidence(areaBySourceId.get(`${item.sourceType}:${item.sourceId}`), item)}
                childId={childId}
                draftId={activeDraftIdentity.draftId}
                preparationDocuments={draft?.preparationDocuments || []}
              />
            ) : null}

            {isCustomJourney && draftLoaded ? (
              <CustomJourneySupportingLink compact accent={accent} onClick={scrollToSupportingInformation} />
            ) : null}

            {draftLoaded ? (
              <JourneyBriefPanel
                childId={childId}
                ownerUserId={child?.users?.care_owner || ''}
                draftIdentity={activeDraftIdentity}
                snapshot={snapshot}
                answers={draft?.answers || []}
                confirmedPriorities={confirmedPriorities}
                canEdit={canEditPreparation}
                accent={accent}
                onOpenReferences={(sourceIds) => openReferences(null, sourceIds)}
                onRevisionStateChange={setRevisionStatus}
                onEditAnswer={isCustomJourney ? updateBriefAnswer : null}
                presentation={isCustomJourney ? 'custom' : 'default'}
                personName={child?.name || ''}
                preparedBy={auth.currentUser?.displayName || ''}
                briefHeader={draft?.briefHeader || null}
                onSaveBriefHeader={updateBriefHeader}
              />
            ) : null}

            <Box ref={supportingInformationRef} sx={{ scrollMarginTop: { xs: 12, md: 20 } }}>
              <Typography component="h2" sx={{ px: 0.5, pt: 0.4, fontSize: '1rem', fontWeight: 800, color: colors.semantic.ink }}>
                Supporting information
              </Typography>
            </Box>
            <Paper elevation={0} sx={{ borderRadius: '24px', overflow: 'hidden', border: `1px solid ${alpha(accent.primary, 0.18)}` }}>
              <JourneySynthesisPanel
                synthesis={snapshot.synthesis}
                accent={accent}
                onOpenReferences={(sourceIds) => openReferences(null, sourceIds)}
              />
              {snapshot.areas.filter((area) => area.id !== 'parent_priorities').map((area, index) => (
                <PreparationJourneyArea
                  key={area.id}
                  area={area}
                  index={index}
                  expanded={expandedAreaId === area.id}
                  sourcesExpanded={Boolean(sourcesExpandedByArea[area.id])}
                  showAllEvents={Boolean(showAllEventsByArea[area.id])}
                  canAdd={canEditPreparation}
                  accent={accent}
                  onToggle={() => setExpandedAreaId((current) => current === area.id ? '' : area.id)}
                  onToggleSources={() => setSourcesExpandedByArea((current) => ({ ...current, [area.id]: !current[area.id] }))}
                  onShowAllEvents={() => setShowAllEventsByArea((current) => ({ ...current, [area.id]: true }))}
                  onOpenEvidence={(item) => openEvidence(area, item)}
                  onOpenReferences={(sourceIds) => openReferences(area, sourceIds)}
                  decidingSourceId={decidingSourceId}
                  onEvidenceDecision={(item, status) => decideEvidence(area, item, status)}
                  onAdd={() => handleAdd(area)}
                />
              ))}
            </Paper>
            <Typography sx={{ px: 0.5, fontSize: '0.78rem', lineHeight: 1.4, color: alpha(colors.semantic.ink, 0.58) }}>
              This checklist only reflects records Lifelog could check. Review important school and health details before your meeting.
            </Typography>
          </Stack>
        ) : null}
      </Container>
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <ChildShellBottomNav activeItem="tools" accent={accent} onSelect={handleBottomNav} />
      </Box>
      <TrackerSourceSheet
        open={Boolean(selectedSource)}
        source={selectedSource}
        onClose={() => setSelectedSource(null)}
        accent={accent}
        theme={theme}
      />
      <PreparationProfileSourceSheet
        open={Boolean(selectedProfileItem)}
        item={selectedProfileItem?.item}
        canEdit={canEditPreparation}
        onClose={() => setSelectedProfileItem(null)}
        onEdit={editSelectedProfileItem}
        onOpenOriginal={() => {
          const source = selectedProfileItem?.item?.originalSourceRef;
          setSelectedProfileItem(null);
          if (source) setSelectedSource(source);
        }}
      />
      <PreparationReferenceSheet
        open={referenceSources.length > 1}
        sources={referenceSources}
        accent={accent}
        onClose={() => setReferenceSources([])}
        onSelect={(item) => {
          setReferenceSources([]);
          openEvidence(item.area, item);
        }}
      />
    </Box>
  );
};

export default IepGuidedCareJourneyPage;
